import React, { useCallback, useContext, useMemo, useState } from 'react';
import {
  Button,
  Card,
  Loader,
  Page,
  PageTitleWithBack,
  FileDrop,
  TabsBar,
  Modal,
  SMBSelect
} from '../../../components';
import { useHistory, useParams } from 'react-router-dom';
import {
  getBuildDocumentationDetails,
  updateBuildDocumentationDetailsLender,
  getBuildDocuments,
  downloadBuildDocument,
  regenerateClosingDocuments,
  regenerateOptionsReport,
  getBuildDocumentTasks,
  uploadTaskBuildDocument,
  uploadMultipleTaskBuildDocuments,
  getDocumentationTaskFiles,
  hideDocumentationFiles,
  updateBuildDocumentationDetailsFinanceType,
  regenerateLandscapeDocument
} from '../../../fetch';
import { useQuery } from 'react-query';
import {
  Cancel,
  Home,
  Save,
  Article,
  Gavel,
  UploadFile,
  LibraryAdd,
  Autorenew
} from '@mui/icons-material';
import {
  Box,
  Checkbox,
  Divider,
  FormControlLabel,
  Grid,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Tab,
  Tabs,
  Typography,
  useMediaQuery
} from '@mui/material';
import { Theme } from '@mui/material/styles';
import makeStyles from '@mui/styles/makeStyles';
import { Item } from '../../development/manage-specs/OptionItem';
import { LenderDropdown } from '../lender-dropdown';
import {
  BuildStatus,
  IBuildDocumentationDetails,
  IBuildDocument,
  IBuildDocumentTask,
  DocumentTaskType,
  UserRoles,
  IDocumentationTaskFile,
  IDropdownResponse
} from '../../../models';
import { useSnackbar } from 'notistack';
import { formatDate, formatMoney } from '../../../helpers';
import { UserContext } from '../../../context';
import { downloadFile, getDownloadFileName } from '../../../helpers';
import { theme } from '../../../styles/theme';
import { useDropzone } from 'react-dropzone';
import { logError } from '../../../services';
import { getFinancingTypes } from '../../../fetch/lookups';
import { allImageFileTypesWithDrawing } from '../../../components/file/utils';

enum ManageDocFilesTabs {
  Upload,
  Manage
}

export const BuildDocumentationDetails = () => {
  const { buildId }: { buildId: string } = useParams();
  const [selectedLender, setSelectedLender] = useState('');
  const { enqueueSnackbar } = useSnackbar();
  const { user } = useContext(UserContext);
  const [activeDropzone, setActiveDropzone] = useState<number | null>(null);
  const [documentationTaskId, setDocumentationTaskId] = useState<number | null>(null);
  const [documentTaskName, setDocumentTaskName] = useState<string | null>(null);
  const [documentType, setDocumentType] = useState<string | null>(null);
  const [isUploading, setIsUploading] = useState<boolean>(false);
  const [manageFilesModalOpen, setManageFilesModalOpen] = useState(false);
  const [selectedFilesToUpload, setSelectedFilesToUpload] = useState<File[]>([]);
  const [selectedFilesToReplace, setSelectedFilesToReplace] = useState<IDocumentationTaskFile[]>(
    []
  );
  const [isUploadingFromModal, setIsUploadingFromModal] = useState<boolean>(false);
  const [selectedModalTab, setSelectedModalTab] = useState<number>(0);
  const [isRemovingFiles, setIsRemovingFiles] = useState<boolean>(false);
  // Lock down documentation management to certain roles
  const canManageDocumentation = useMemo(
    () =>
      user.userRoles.includes(UserRoles.BLAdmin) ||
      user.userRoles.includes(UserRoles.BLField) ||
      user.userRoles.includes(UserRoles.BLFieldAdmin) ||
      user.userRoles.includes(UserRoles.BLClosing),
    [user]
  );

  const classes = useStyles();
  const isMobile = useMediaQuery(`(max-width: 900px)`);

  const {
    isLoading: isLoadingBuildDetails,
    data: buildDetails,
    refetch: refetchBuildDetails,
    isRefetching: isRefetchingBuildDetails
  } = useQuery<IBuildDocumentationDetails, Error>(
    ['getDocumentationBuildDetails'],
    () => getBuildDocumentationDetails(buildId),
    {
      notifyOnChangeProps: 'tracked',
      enabled: !!buildId,
      onSuccess: data => {
        setSelectedLender(data.lenderId);
      }
    }
  );

  const {
    isLoading: isLoadingContractDocuments,
    data: contractDocumentResults,
    refetch: refetchContractDocuments
  } = useQuery<IBuildDocument[], Error>(
    ['build-documents-data', buildId],
    async () => await getBuildDocuments(buildId),
    {
      notifyOnChangeProps: 'tracked',
      enabled: !!buildId
    }
  );

  const {
    isLoading: isLoadingBuildDocumentTasks,
    data: buildDocumentTasks,
    refetch: refetchBuildDocumentTasks
  } = useQuery<IBuildDocumentTask[], Error>(
    ['build-documents-tasks', buildId],
    async () => await getBuildDocumentTasks(buildId),
    {
      notifyOnChangeProps: 'tracked',
      enabled: !!buildId
    }
  );

  const {
    isLoading: isLoadingDocumentationTaskFiles,
    data: documentationTaskFiles,
    refetch: refetchDocumentationTaskFiles
  } = useQuery<IDocumentationTaskFile[], Error>(
    ['build-documents-tasks', documentationTaskId, manageFilesModalOpen, documentType],
    async () => await getDocumentationTaskFiles(+buildId, documentationTaskId),
    {
      notifyOnChangeProps: 'tracked',
      enabled:
        !!buildId &&
        !!documentationTaskId &&
        manageFilesModalOpen &&
        documentType === DocumentTaskType.MultiFileUpload
    }
  );

  const uploadDZProps = useDropzone({
    accept: {
      'application/pdf': ['.pdf'],
      'image/*': [],
      'application/dwg': ['.acad', '.dwg']
    },
    useFsAccessApi: false,
    onDrop: acceptedFiles => {
      if (acceptedFiles.length > 0) {
        const acceptedFile = acceptedFiles;
        if (manageFilesModalOpen) {
          setSelectedFilesToUpload(acceptedFile);
        } else {
          handleSave(acceptedFile);
        }
      } else {
        enqueueSnackbar(
          `Invalid file type. Please upload one of these image types, ${allImageFileTypesWithDrawing.join(
            ', '
          )}.`,
          {
            variant: 'error'
          }
        );
      }
    }
  });

  const handleSave = async (buildDocumentData: any) => {
    try {
      let snackBarMessage = 'Success, file uploaded!';
      setIsUploading(true);

      if (documentType === DocumentTaskType.MultiFileUpload) {
        const formData = new FormData();
        buildDocumentData.forEach((file: File) => {
          formData.append(
            'BuildDocumentFiles',
            // custom check here so we can pass the correct content-type for .dwg files
            file.name.includes('.dwg') ? new Blob([file], { type: 'application/acad' }) : file,
            file.name
          );
        });

        await uploadMultipleTaskBuildDocuments(
          buildId,
          formData,
          documentationTaskId,
          documentTaskName
        );
        snackBarMessage = 'Success, files uploaded!';
      } else {
        const formData = new FormData();
        formData.append(
          'BuildDocumentFile',
          // custom check here so we can pass the correct content-type for .dwg files
          buildDocumentData?.[0]?.name?.includes('.dwg')
            ? new Blob([buildDocumentData?.[0]], { type: 'application/acad' })
            : buildDocumentData[0],
          buildDocumentData[0].name
        );

        await uploadTaskBuildDocument(buildId, formData, documentationTaskId, documentTaskName);
      }
      await refetchBuildDocumentTasks();
      await refetchContractDocuments();
      setIsUploading(false);
      setActiveDropzone(null);
      enqueueSnackbar(snackBarMessage, {
        variant: 'success'
      });
    } catch (error) {
      enqueueSnackbar(error?.Detail ?? `Error uploading file(s), please try again.`, {
        variant: 'error'
      });
      setIsUploading(false);
      logError(error, 'handleSave failed');
    }
  };

  const handleDownloadDocument = async (buildDocumentId: number, fileName: string) => {
    try {
      const res = await downloadBuildDocument(buildDocumentId);
      downloadFile(res?.data, getDownloadFileName(res) || fileName);
    } catch (error) {
      enqueueSnackbar(error?.Detail ?? `Error downloading document, please try again.`, {
        variant: 'error'
      });
    }
  };

  const downloadBlobFile = (url, filename) => {
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const [lenderIsSaving, setLenderIsSaving] = useState(false);

  const [displayLender, setDisplayLender] = useState(false);
  const handleDisplayLender = () => {
    setDisplayLender(!displayLender);
  };

  const handleSaveLender = async () => {
    try {
      setLenderIsSaving(true);
      // Save the selected lender
      await updateBuildDocumentationDetailsLender(buildId, selectedLender);
      refetchBuildDetails();
      enqueueSnackbar('Lender saved!', {
        variant: 'success'
      });
      setSelectedLender(selectedLender);
      handleDisplayLender();
    } catch (error) {
      enqueueSnackbar(error?.Detail ?? 'Error saving Lender.', {
        variant: 'error'
      });
    } finally {
      setLenderIsSaving(false);
    }
  };

  const handleCancelLender = async () => {
    handleDisplayLender();
    setSelectedLender(buildDetails?.lenderId);
  };

  const isCash = buildDetails?.financingType === 'Cash';
  const showLenderDropdown = useCallback(() => {
    if (isCash) {
      return false;
    }
    if (buildDetails?.buildStatus === BuildStatus.New) {
      return false;
    }
    if (buildDetails?.buildStatus === BuildStatus.BuildApproved) {
      return false;
    }
    return true;
  }, [buildDetails?.buildStatus, isCash]);

  const userRoleCanUpdateLender = user.userRoles.includes(UserRoles.BLAdmin);
  const userRoleCanUpdateFinancingType = user.userRoles.includes(UserRoles.BLAdmin);

  // DAMP variable on purpose: semantic difference. There was some conversation about which UserRoles
  // should be able to see admin actions. This seems to repeat, but it's a different semantic meaning.
  const userRoleHasAdminActions = user.userRoles.includes(UserRoles.BLAdmin);

  const isMedium = useMediaQuery(theme.breakpoints.up('md'));

  const history = useHistory();

  const [isRegeneratingClosingDocuments, setIsRegeneratingClosingDocuments] = useState(false);
  const handleRegenerateClosingDocuments = async () => {
    try {
      setIsRegeneratingClosingDocuments(true);
      // Save the selected lender
      await regenerateClosingDocuments(buildId);
      refetchContractDocuments();
      enqueueSnackbar('Closing documents regenerated!', {
        variant: 'success'
      });
    } catch (error) {
      enqueueSnackbar(error?.Detail ?? 'Error regenerating closing documents.', {
        variant: 'error'
      });
    } finally {
      setIsRegeneratingClosingDocuments(false);
    }
  };
  const [isRegeneratingLandscapeDocument, setIsRegeneratingLandscapeDocument] = useState(false);
  const handleRegenerateLandscapeDocument = async () => {
    try {
      setIsRegeneratingLandscapeDocument(true);
      // Regenerate the landscape document
      await regenerateLandscapeDocument(buildId);
      enqueueSnackbar('Landscape document regenerated!', {
        variant: 'success'
      });
    } catch (error) {
      enqueueSnackbar(error?.Detail ?? 'Error regenerating landscape document.', {
        variant: 'error'
      });
    } finally {
      setIsRegeneratingLandscapeDocument(false);
    }
  };
  const [isRegeneratingOptionsReport, setIsRegeneratingOptionsReport] = useState(false);
  const handleRegenerateOptionsReport = async () => {
    try {
      setIsRegeneratingOptionsReport(true);
      // Save the selected lender
      await regenerateOptionsReport(buildId);
      enqueueSnackbar('Options report regenerated!', {
        variant: 'success'
      });
    } catch (error) {
      enqueueSnackbar(error?.Detail ?? 'Error regenerating options report.', {
        variant: 'error'
      });
    } finally {
      setIsRegeneratingOptionsReport(false);
    }
  };
  const handleReplaceFinalContract = () => {
    history.push(`/sales/contracting/under-contract/${buildId}/contract-files`);
  };
  const handleAdjustments = () => {
    history.push(`/sales/contracting/contract-acceptance/build-details/${buildId}`);
  };
  const handleManageFilesModalClose = () => {
    setManageFilesModalOpen(false);
    setSelectedFilesToUpload([]);
    setSelectedFilesToReplace([]);
  };
  const handleManageFilesModaleSave = async (buildDocumentData: any) => {
    try {
      setIsUploadingFromModal(true);
      selectedFilesToReplace.length > 0 && (await handleRemoveFiles());

      const formData = new FormData();
      buildDocumentData.forEach((file: File) => {
        formData.append(
          'BuildDocumentFiles',
          // custom check here so we can pass the correct content-type for .dwg files
          file.name?.includes('.dwg') ? new Blob([file], { type: 'application/acad' }) : file,
          file.name
        );
      });

      await uploadMultipleTaskBuildDocuments(
        buildId,
        formData,
        documentationTaskId,
        documentTaskName
      );
      enqueueSnackbar('Success, files uploaded!', {
        variant: 'success'
      });
      setSelectedModalTab(ManageDocFilesTabs.Manage);
      refetchDocumentationTaskFiles();
    } catch (error) {
      enqueueSnackbar(error?.Detail ?? `Error uploading file(s), please try again.`, {
        variant: 'error'
      });
      logError(error, 'handleManageFilesModaleSave failed');
    } finally {
      setIsUploadingFromModal(false);
      setSelectedFilesToUpload([]);
      refetchContractDocuments();
    }
  };

  const handleRemoveFiles = async () => {
    try {
      setIsRemovingFiles(true);
      const filesToHide = selectedFilesToReplace.map(file => {
        return {
          buildDocumentId: file.id,
          hideInDocumentationPortal: true
        };
      });
      await hideDocumentationFiles(filesToHide);
      enqueueSnackbar('Success, files have been removed!', {
        variant: 'success'
      });
    } catch (err: any) {
      enqueueSnackbar(err?.Detail ?? `Error removing file(s), please try again.`, {
        variant: 'error'
      });
      logError(err, 'handleRemoveFiles failed');
    } finally {
      setIsRemovingFiles(false);
      refetchDocumentationTaskFiles();
      setSelectedFilesToReplace([]);
    }
  };

  const [displayFinancingTypeDropdown, setDisplayFinancingTypeDropdown] = useState(false);
  const handleDisplayFinancingTypeDropdown = () => {
    setDisplayFinancingTypeDropdown(!displayFinancingTypeDropdown);
  };
  const [selectedFinancingType, setSelectedFinancingType] = useState<string | null>(null);
  const handleCancelChangeFinancingType = () => {
    handleDisplayFinancingTypeDropdown();
    setSelectedFinancingType(null);
  };
  const [financingTypeIsSaving, setFinancingTypeIsSaving] = useState(false);
  const handleSaveFinancingType = async () => {
    try {
      setFinancingTypeIsSaving(true);
      // Save the selected financing type
      await updateBuildDocumentationDetailsFinanceType(buildId, selectedFinancingType);
      refetchBuildDetails();
      enqueueSnackbar('Financing Type saved!', {
        variant: 'success'
      });
      setSelectedFinancingType(null);
      handleDisplayFinancingTypeDropdown();
    } catch (error) {
      enqueueSnackbar(error?.Detail ?? 'Error saving Financing Type.', {
        variant: 'error'
      });
    } finally {
      setFinancingTypeIsSaving(false);
    }
  };

  const showChangeLoanType = useCallback(() => {
    if (isCash) {
      return false;
    }
    if (buildDetails?.buildStatus === BuildStatus.New) {
      return false;
    }
    if (buildDetails?.buildStatus === BuildStatus.BuildApproved) {
      return false;
    }
    return true;
  }, [buildDetails?.buildStatus, isCash]);

  return (
    <Page title="Build Documentation Details">
      <PageTitleWithBack
        title={`Build Documentation Details`}
        backUrl={'/documentation/build-documentation'}
        backText={'Back to Build Documentation'}
      />
      {isUploading && (
        <Loader type="fullscreen" position="centered">
          Loading...
        </Loader>
      )}
      <Modal
        open={manageFilesModalOpen}
        onClose={handleManageFilesModalClose}
        maxWidth="sm"
        title={`Manage Files for "${documentTaskName}"`}
      >
        <TabsBar>
          <Tabs
            value={selectedModalTab}
            indicatorColor="primary"
            textColor="primary"
            onChange={(_, value) => {
              setSelectedModalTab(value);
            }}
          >
            <Tab id="upload" aria-controls="upload" label="Upload" />
            <Tab id="manage" aria-controls="manage" label="Manage" />
          </Tabs>
        </TabsBar>
        {selectedModalTab === ManageDocFilesTabs.Upload && (
          <Box mt={1} mb={1.5}>
            <Typography mb={1}>
              Optional: From Manage tab, select File(s) to remove during upload.
            </Typography>
            {!isUploadingFromModal && (
              <>
                <FileDrop id="File-drop-modal" {...uploadDZProps} title="">
                  <Typography variant="body1">
                    Drag and drop a file or{' '}
                    <Box
                      component="a"
                      href="/#"
                      sx={{ color: theme => theme.palette.primary.main, textDecoration: 'none' }}
                      onClick={e => e.preventDefault()}
                    >
                      browse and pick
                    </Box>{' '}
                  </Typography>
                </FileDrop>
                {selectedFilesToUpload.length > 0 && (
                  <List>
                    {selectedFilesToUpload.map((file: any) => (
                      <ListItem key={file.path}>{file.path}</ListItem>
                    ))}
                  </List>
                )}
              </>
            )}
            {isUploadingFromModal && (
              <Loader type="inline" position="centered">
                Uploading...
              </Loader>
            )}
          </Box>
        )}
        {selectedModalTab === ManageDocFilesTabs.Manage && (
          <Box mb={1} mt={1}>
            {isLoadingDocumentationTaskFiles && <Loader position="centered" type="inline" />}
            {!isLoadingDocumentationTaskFiles && documentationTaskFiles?.length > 0 && (
              <>
                <Grid container justifyContent={'center'}>
                  <Grid item xs={12} sm={6}>
                    <Typography>Select File(s) to replace or remove</Typography>
                  </Grid>
                  <Grid item xs={12} sm={6} display={'flex'} justifyContent={'flex-end'}>
                    <Button
                      color={'secondary'}
                      variant={'contained'}
                      id={'remove-selected-files'}
                      onClick={async () => {
                        await handleRemoveFiles();
                        refetchContractDocuments();
                      }}
                      disabled={
                        selectedFilesToReplace.length === 0 ||
                        isUploadingFromModal ||
                        isRemovingFiles
                      }
                      fullWidth={isMobile ? true : false}
                    >
                      Remove Selected
                    </Button>
                  </Grid>
                </Grid>
                <List>
                  {documentationTaskFiles?.map((file, index) => (
                    <ListItem key={file?.id ? file.id : `file-${index}`}>
                      <FormControlLabel
                        color="primary"
                        control={
                          <Checkbox
                            id="existing-documents-checkbox"
                            name="existing-documents-checkbox"
                            checked={selectedFilesToReplace.includes(file)}
                            disabled={isUploadingFromModal}
                            onChange={() => {
                              selectedFilesToReplace.includes(file)
                                ? setSelectedFilesToReplace(
                                    selectedFilesToReplace.filter(selectedFile => {
                                      return selectedFile.id !== file.id;
                                    })
                                  )
                                : setSelectedFilesToReplace([...selectedFilesToReplace, file]);
                            }}
                          />
                        }
                        label={file?.name ?? 'N/A'}
                      />
                    </ListItem>
                  ))}
                </List>
              </>
            )}
            {!isLoadingDocumentationTaskFiles && documentationTaskFiles?.length === 0 && (
              <Typography textAlign="center">No existing files found</Typography>
            )}
          </Box>
        )}
        <Box mt={1} mb={1.5}>
          <hr className={classes.hr} />
        </Box>
        <Box>
          <Button
            id={'manage-files-modal-save-button'}
            onClick={() => handleManageFilesModaleSave(selectedFilesToUpload)}
            variant="contained"
            color="primary"
            fullWidth
            disabled={isUploadingFromModal || selectedFilesToUpload.length === 0}
          >
            Upload
          </Button>
        </Box>
      </Modal>
      <Box display={'block'} displayPrint={'none'}>
        {/* This card may be better refactored out. We are saving Lender on change, */}
        <Grid container spacing={isMedium ? 2 : 0}>
          <Grid item xs={12} md={9}>
            <Card icon={<Home />} title="Details">
              {isLoadingBuildDetails || isRefetchingBuildDetails ? (
                <Loader position="centered" type="inline">
                  Loading...
                </Loader>
              ) : (
                <Grid container spacing={1}>
                  <Grid item xs={12} md={6}>
                    <Item
                      alignItems="flex-start"
                      label="Neighborhood: "
                      value={buildDetails?.neighborhoodDescription}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Item alignItems="flex-start" label="Lot: " value={buildDetails?.lotNumber} />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Item
                      alignItems="flex-start"
                      label="Sage Job ID: "
                      value={buildDetails?.sageJobId}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Item
                      alignItems="flex-start"
                      label="Address: "
                      value={buildDetails?.addressDisplay}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Item
                      alignItems="flex-start"
                      label="Plan: "
                      value={buildDetails?.planDescription}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Item
                      alignItems="flex-start"
                      label="Purchaser: "
                      value={buildDetails?.contractName}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Item
                      alignItems="flex-start"
                      label="Purchaser Email:"
                      value={buildDetails?.contractEmail}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Item
                      alignItems="flex-start"
                      label="Purchaser Phone:"
                      value={buildDetails?.contractPhone}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Item
                      alignItems="flex-start"
                      label={
                        buildDetails?.isClosingDateLocked
                          ? 'Closing Date (Locked): '
                          : 'Closing Date (Estimated): '
                      }
                      value={formatDate(buildDetails?.closingDate)}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Item
                      alignItems="flex-start"
                      label="Total Purchase Price: "
                      value={formatMoney(buildDetails?.totalPurchasePrice)}
                    />
                  </Grid>
                  <Grid item container xs={12} md={6}>
                    <Grid item xs={12} mb={0.5}>
                      <Item
                        alignItems="flex-start"
                        label="Lender: "
                        value={isCash ? 'Cash Purchase - No Lender' : buildDetails?.lenderDisplay}
                      />
                    </Grid>
                    {!displayLender && showLenderDropdown() && (
                      <Grid item xs={12} md={6}>
                        <Button
                          disabled={!userRoleCanUpdateLender}
                          variant="contained"
                          color="primary"
                          onClick={handleDisplayLender}
                          id={'displayLender-hidden'}
                          fullWidth={isMobile ? true : false}
                        >
                          Change Lender
                        </Button>
                      </Grid>
                    )}
                    {displayLender && showLenderDropdown() && (
                      <>
                        <Grid item xs={8} md={6}>
                          <LenderDropdown
                            neighborhoodId={buildDetails?.neighborhoodId as number}
                            value={selectedLender}
                            isDisabled={lenderIsSaving || !userRoleCanUpdateLender}
                            handleChange={(val: string) => {
                              setSelectedLender(val);
                            }}
                            handleClear={() => setSelectedLender('')}
                            isEnabled={!!buildDetails}
                          />
                        </Grid>
                        <Grid item xs={4} justifyContent={'space-around'}>
                          <IconButton
                            disabled={lenderIsSaving}
                            title={'save'}
                            color="primary"
                            onClick={() => handleSaveLender()}
                          >
                            <Save />
                          </IconButton>

                          <IconButton
                            disabled={lenderIsSaving}
                            title={'cancel'}
                            color="error"
                            onClick={handleCancelLender}
                          >
                            <Cancel />
                          </IconButton>
                        </Grid>
                      </>
                    )}
                  </Grid>
                  <Grid item container xs={12} md={6}>
                    <Grid item xs={12} mb={0.5}>
                      <Item
                        alignItems="flex-start"
                        label="Financing Type: "
                        value={buildDetails?.financingType}
                      />
                    </Grid>
                    {!displayFinancingTypeDropdown && showChangeLoanType() && (
                      <Grid item xs={12}>
                        <Button
                          variant="contained"
                          color="primary"
                          onClick={handleDisplayFinancingTypeDropdown}
                          id={'displayFinancingType-hidden'}
                          fullWidth={isMobile ? true : false}
                          disabled={!userRoleCanUpdateFinancingType}
                        >
                          Change Financing Type
                        </Button>
                      </Grid>
                    )}
                    {displayFinancingTypeDropdown && showChangeLoanType() && (
                      <>
                        <Grid item xs={8} md={6}>
                          <SMBSelect
                            label="Financing Type"
                            name="financingType"
                            apiRequest={() => getFinancingTypes()}
                            shouldFetch
                            value={selectedFinancingType ?? buildDetails?.financingType}
                            transformResponse={(res: IDropdownResponse[]) =>
                              res.map(r => ({
                                label: r.description,
                                value: r.value
                              }))
                            }
                            onChange={e => setSelectedFinancingType(e.target.value)}
                            disabled={financingTypeIsSaving || !userRoleCanUpdateFinancingType}
                          />
                        </Grid>
                        <Grid item xs={4} justifyContent={'space-around'}>
                          <IconButton
                            disabled={financingTypeIsSaving}
                            title={'save'}
                            color="primary"
                            onClick={() => handleSaveFinancingType()}
                          >
                            <Save />
                          </IconButton>

                          <IconButton
                            disabled={financingTypeIsSaving}
                            title={'cancel'}
                            color="error"
                            onClick={handleCancelChangeFinancingType}
                          >
                            <Cancel />
                          </IconButton>
                        </Grid>
                      </>
                    )}
                  </Grid>
                </Grid>
              )}
            </Card>
            {canManageDocumentation && (
              <Card icon={<Article />} title="Documentation">
                {isLoadingBuildDocumentTasks ? (
                  <Loader position="centered" type="inline">
                    Loading...
                  </Loader>
                ) : (
                  <Box p={0.5}>
                    {buildDocumentTasks?.map((task, index) => (
                      <Grid
                        container
                        key={task?.documentationTaskId}
                        spacing={1}
                        alignItems="center"
                      >
                        <Grid item xs={12} sm={6}>
                          <Item label={task?.name ?? 'N/A'} value={''} />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <>
                            {activeDropzone === task.documentationTaskId ? (
                              <>
                                <IconButton
                                  aria-label="hide dropzone"
                                  onClick={() => {
                                    setActiveDropzone(null);
                                    setDocumentTaskName(null);
                                    setDocumentationTaskId(null);
                                    setDocumentType(null);
                                  }}
                                >
                                  <Cancel />
                                </IconButton>
                                <FileDrop id="File-drop" {...uploadDZProps} title="">
                                  <Typography variant="body1">
                                    Drag and drop a file or{' '}
                                    <Box
                                      component="a"
                                      href="/#"
                                      sx={{
                                        color: theme => theme.palette.primary.main,
                                        textDecoration: 'none'
                                      }}
                                      onClick={e => e.preventDefault()}
                                    >
                                      browse and pick
                                    </Box>{' '}
                                  </Typography>
                                </FileDrop>
                              </>
                            ) : (
                              task.type !== DocumentTaskType.MultiFileUpload &&
                              task.type !== DocumentTaskType.LandscapeFileUpload && (
                                <Button
                                  disabled={isUploading}
                                  id={'upload-document'}
                                  onClick={() => {
                                    setActiveDropzone(task.documentationTaskId);
                                    setDocumentTaskName(task.name);
                                    setDocumentationTaskId(task.documentationTaskId);
                                    setDocumentType(task.type);
                                  }}
                                  variant="contained"
                                  color={task.hasDocuments ? 'secondary' : 'primary'}
                                  startIcon={<UploadFile />}
                                  fullWidth
                                >
                                  Upload File
                                </Button>
                              )
                            )}
                            {task.type === DocumentTaskType.MultiFileUpload && (
                              <Button
                                disabled={isUploading}
                                id={'upload-document'}
                                onClick={() => {
                                  setDocumentTaskName(task.name);
                                  setDocumentationTaskId(task.documentationTaskId);
                                  setDocumentType(task.type);
                                  setManageFilesModalOpen(true);
                                }}
                                variant="contained"
                                color={task.hasDocuments ? 'secondary' : 'primary'}
                                startIcon={<LibraryAdd />}
                                fullWidth
                              >
                                Manage Files
                              </Button>
                            )}
                            {task.type === DocumentTaskType.LandscapeFileUpload && (
                              <Button
                                disabled={isRegeneratingLandscapeDocument}
                                id={'regenerate-landscape-document'}
                                onClick={handleRegenerateLandscapeDocument}
                                variant="contained"
                                color={task.hasDocuments ? 'secondary' : 'primary'}
                                startIcon={<Autorenew />}
                                fullWidth
                              >
                                Regenerate
                              </Button>
                            )}
                          </>
                        </Grid>
                        {index !== buildDocumentTasks.length - 1 && (
                          <Grid item xs={12} pb={1}>
                            <Divider />
                          </Grid>
                        )}
                      </Grid>
                    ))}
                  </Box>
                )}
              </Card>
            )}
          </Grid>

          <Grid item xs={12} md={3}>
            {userRoleHasAdminActions && (
              <Box>
                <Card icon={<Gavel />} title="Administrative Actions">
                  <List sx={{ marginTop: '-15px' }}>
                    <ListItem
                      disablePadding
                      button
                      disabled={isRegeneratingClosingDocuments}
                      key={'Regenerate Closing Documents'}
                      onClick={handleRegenerateClosingDocuments}
                    >
                      <ListItemText
                        primary={'Regenerate Closing Documents'}
                        className={classes.itemText}
                      />
                    </ListItem>

                    <ListItem
                      disablePadding
                      button
                      disabled={isRegeneratingOptionsReport}
                      key={'Regenerate Options Report'}
                      onClick={handleRegenerateOptionsReport}
                    >
                      <ListItemText
                        primary={'Regenerate Options Report'}
                        className={classes.itemText}
                      />
                    </ListItem>

                    <ListItem
                      disablePadding
                      button
                      key={'Replace Final Contract'}
                      onClick={() => handleReplaceFinalContract()}
                    >
                      <ListItemText
                        primary={'Replace Final Contract'}
                        className={classes.itemText}
                      />
                    </ListItem>

                    <ListItem
                      disablePadding
                      button
                      key={'Adjustments'}
                      onClick={() => handleAdjustments()}
                    >
                      <ListItemText primary={'Adjustments'} className={classes.itemText} />
                    </ListItem>
                  </List>
                </Card>
              </Box>
            )}
            <Box>
              <Card icon={<Article />} title="Build Documents">
                <List sx={{ marginTop: '-15px' }}>
                  {!isLoadingContractDocuments &&
                  (contractDocumentResults?.length === 0 ||
                    !contractDocumentResults ||
                    !contractDocumentResults[0]) ? (
                    <ListItem dense disablePadding>
                      <Typography>There are no build documents to display.</Typography>
                    </ListItem>
                  ) : (
                    <>
                      {contractDocumentResults?.map((doc, index) => {
                        if (doc?.buildDocumentId !== 0) {
                          return (
                            <ListItem
                              disablePadding
                              button
                              key={doc?.buildDocumentId ? doc.buildDocumentId : `doc-${index}`}
                              onClick={() => handleDownloadDocument(doc.buildDocumentId, doc.name)}
                            >
                              <ListItemText
                                primary={doc?.name ?? 'N/A'}
                                className={classes.itemText}
                              />
                            </ListItem>
                          );
                        } else {
                          return (
                            <ListItem
                              disablePadding
                              button
                              key={doc?.buildDocumentId ? doc.buildDocumentId : `doc-${index}`}
                              onClick={() => downloadBlobFile(doc.downloadPath, doc.name)}
                            >
                              <ListItemText
                                primary={doc?.name ?? 'N/A'}
                                className={classes.itemText}
                              />
                            </ListItem>
                          );
                        }
                      })}
                    </>
                  )}
                </List>
                {isLoadingContractDocuments && (
                  <Box>
                    <Loader position="left" type="inline">
                      Loading...
                    </Loader>
                  </Box>
                )}
              </Card>
            </Box>
          </Grid>
        </Grid>
      </Box>
    </Page>
  );
};

const useStyles = makeStyles((theme: Theme) => ({
  itemText: {
    fontWeight: 600,
    fontSize: '1.1rem',
    color: 'rgb(97, 138, 44)'
  },
  hr: {
    border: 'none',
    borderTop: '1px solid #e0e0e0',
    margin: '8px 0'
  }
}));
